/**
 * Pfeile fuer eine horizontal scrollbare Produktreihe.
 *
 * Bewusst klein gehalten: Horizons Slideshow-Komponente bringt Punkte,
 * Autoplay und Endlosschleife mit, die hier alle nicht gebraucht werden.
 * Gescrollt wird nativ - die Pfeile setzen nur die Position, damit Tastatur,
 * Trackpad und Wischgesten weiter wie gewohnt funktionieren.
 *
 * Am jeweiligen Ende wird der Pfeil deaktiviert. Das CSS blendet ihn dann aus;
 * `disabled` haelt ihn zugleich aus der Tabreihenfolge und aus der Vorlesereihe.
 */
/*
 * Zeitachse des Wischhinweises - dieselbe wie beim Hinweis auf der
 * Produktseite: bei 18 Prozent setzt sich der Punkt in Bewegung, bei 48 ist er
 * am weitesten, bei 78 wieder am Platz.
 */
const WISCH_DAUER = 2200;
const WISCH_WEG = 72;
const WISCH_BEGINN = 0.18;
const WISCH_OBEN = 0.48;
const WISCH_ENDE = 0.78;

/**
 * Darf dieser Hinweis noch einmal gezeigt werden?
 *
 * Hoechstens zweimal je Besuch - danach kennt man ihn. Gezaehlt wird im
 * Sitzungsspeicher: er gilt fuer den Tab und ist beim naechsten Besuch wieder
 * leer. Dieselbe Zaehlung steht in dp-bild-overlay.js, dp-galerie-hinweis.js
 * und dp-scroller.js; jede fuehrt ihren eigenen Eintrag.
 *
 * Gezaehlt wird erst beim tatsaechlichen Zeigen, nicht beim Pruefen der
 * uebrigen Bedingungen.
 *
 * @returns {boolean}
 */
function darfHinweisZeigen() {
  const schluessel = 'dp-hinweis-reihe';

  try {
    const bisher = Number(window.sessionStorage.getItem(schluessel) || '0');
    if (bisher >= HINWEIS_HOECHSTENS) return false;

    window.sessionStorage.setItem(schluessel, String(bisher + 1));
    return true;
  } catch {
    /*
     * Im privaten Fenster ist der Speicher gesperrt. Dann eben ohne Zaehlung -
     * ein Hinweis ist kein Grund, hier auszusteigen.
     */
    return true;
  }
}

/** Wie oft ein Hinweis je Besuch hoechstens erscheint. */
const HINWEIS_HOECHSTENS = 2;


/**
 * Weiches An- und Auslaufen.
 *
 * @param {number} t Anteil zwischen 0 und 1.
 * @returns {number}
 */
function weich(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

class DpScroller extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-dp-track]');
    this.prev = this.querySelector('[data-dp-prev]');
    this.next = this.querySelector('[data-dp-next]');
    // Optional: zeigt an, das wievielte Element gerade zu sehen ist.
    this.zaehler = this.querySelector('[data-dp-zaehler]');
    if (!this.track) return;

    this.update = this.update.bind(this);

    this.prev?.addEventListener('click', () => this.scrollByCard(-1));
    this.next?.addEventListener('click', () => this.scrollByCard(1));
    this.track.addEventListener('scroll', this.update, { passive: true });

    // Teilweise verdeckte Karten beim Ueberfahren vollstaendig hereinholen -
    // sonst laesst sich das Schnellkauf-Feld daran nicht bedienen.
    this.handleHover = this.handleHover.bind(this);
    this.track.addEventListener('pointerover', this.handleHover);

    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.update);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.update);
    }

    this.update();
    this.beobachteSichtbarkeit();
  }

  /*
   * Auf dem Handy ist nicht zu sehen, dass die Reihe weitergeht - Pfeile gibt
   * es dort nicht, und ein angeschnittenes Bild am Rand uebersieht man leicht.
   * Sobald die Reihe ins Bild kommt, ruckt sie deshalb einmal kurz an und
   * wieder zurueck. Das zeigt in einer Bewegung, dass sich hier wischen laesst.
   */
  beobachteSichtbarkeit() {
    if (!('IntersectionObserver' in window)) return;

    /*
     * Erst wenn die Reihe vollstaendig im Bild steht - deshalb der Schwellwert
     * von eins. Ragt sie noch halb aus dem Bildschirm, ist von der Bewegung
     * wenig zu sehen und der Punkt steht womoeglich ausserhalb.
     */
    this.blick = new IntersectionObserver(
      (eintraege) => {
        for (const eintrag of eintraege) {
          if (!eintrag.isIntersecting) continue;
          if (this.hinweisGeplant) continue;

          this.hinweisGeplant = true;

          /*
           * Mit Bedacht etwas spaeter: kommt die Reihe gerade erst ins Bild,
           * scrollt die Seite meist noch. Eine Bewegung mitten hinein geht
           * unter oder wirkt wie ein Ruckler.
           *
           * Danach wird noch einmal nachgesehen: in der Zwischenzeit kann die
           * Reihe wieder aus dem Bild gescrollt sein. Dann bleibt der
           * Beobachter stehen und wartet auf das naechste Mal.
           */
          window.setTimeout(() => {
            this.hinweisGeplant = false;

            if (!this.ganzImBild()) return;

            this.zeigeWischhinweis();
            this.blick.disconnect();
          }, 700);
        }
      },
      { threshold: 1 }
    );

    this.blick.observe(this);
  }

  /**
   * Steht die Reihe vollstaendig im Bild?
   *
   * @returns {boolean}
   */
  ganzImBild() {
    const rand = this.getBoundingClientRect();
    const hoehe = window.innerHeight || document.documentElement.clientHeight;

    return rand.top >= 0 && rand.bottom <= hoehe;
  }

  zeigeWischhinweis() {
    if (this.hinweisGezeigt) return;

    // Nur dort, wo gewischt wird - wo es Pfeile gibt (Maus, Touchpad), fuehren die.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Nichts anzudeuten, wenn ohnehin alles zu sehen ist oder schon gewischt wurde.
    const maximum = this.track.scrollWidth - this.track.clientWidth;
    if (maximum < 24 || this.track.scrollLeft > 2) return;

    if (!darfHinweisZeigen()) return;

    this.hinweisGezeigt = true;

    /*
     * Ein Punkt am rechten Rand wischt nach links, die Reihe laeuft dieselbe
     * Strecke mit - dieselbe Sprache wie die Hinweise auf der Produktseite.
     *
     * Beides kommt aus einer einzigen Schleife: ein Wert je Bild, dreifach
     * verwendet. Zwei getrennte Animationen liefen nie ganz gleich.
     */
    const weg = Math.min(WISCH_WEG, maximum);

    const hinweis = document.createElement('span');
    hinweis.className = 'dp-wischhinweis';
    hinweis.setAttribute('aria-hidden', 'true');
    hinweis.innerHTML = '<span class="dp-wischhinweis__punkt"></span>';
    this.appendChild(hinweis);

    const punkt = hinweis.firstElementChild;
    let abgebrochen = false;

    const beenden = () => {
      abgebrochen = true;
      hinweis.remove();
      for (const art of ['pointerdown', 'touchstart', 'wheel']) {
        this.removeEventListener(art, beenden);
      }
    };

    // Wer selbst wischt, braucht die Vorfuehrung nicht mehr.
    for (const art of ['pointerdown', 'touchstart', 'wheel']) {
      this.addEventListener(art, beenden, { passive: true, once: true });
    }

    // Abgeraeumt wird erst, wenn auch das Ausblenden durch ist.
    window.setTimeout(beenden, WISCH_DAUER);

    const beginn = performance.now();

    const schritt = (jetzt) => {
      if (abgebrochen) return;

      const t = (jetzt - beginn) / WISCH_DAUER;

      if (t >= WISCH_ENDE) {
        this.track.scrollLeft = 0;
        this.update();

        /*
         * Hier endet nur die Bewegung, nicht der Hinweis: das Ausblenden
         * laeuft noch bis zum Ende der Zeitachse. Wer den Punkt an dieser
         * Stelle entfernt, schneidet es ab und er verschwindet schlagartig.
         */
        return;
      }

      let weite = 0;
      if (t > WISCH_BEGINN && t <= WISCH_OBEN) {
        weite = weg * weich((t - WISCH_BEGINN) / (WISCH_OBEN - WISCH_BEGINN));
      } else if (t > WISCH_OBEN) {
        weite = weg * (1 - weich((t - WISCH_OBEN) / (WISCH_ENDE - WISCH_OBEN)));
      }

      this.track.scrollLeft = Math.round(weite);
      punkt.style.transform = 'translateX(' + -weite.toFixed(1) + 'px)';
      this.update();

      window.requestAnimationFrame(schritt);
    };

    window.requestAnimationFrame(schritt);
  }

  disconnectedCallback() {
    this.blick?.disconnect();
    this.observer?.disconnect();
    window.removeEventListener('resize', this.update);
    this.track?.removeEventListener('scroll', this.update);
    if (this.handleHover) this.track?.removeEventListener('pointerover', this.handleHover);
  }

  /**
   * Holt eine angefahrene Karte vollstaendig in den Sichtbereich, falls sie
   * am Rand angeschnitten ist.
   *
   * @param {PointerEvent} ereignis
   */
  handleHover(ereignis) {
    /*
     * Nur mit der Maus. Auf dem Handy loest ein Wischen dasselbe Ereignis aus -
     * der Slider zoege die angetippte Karte dann zurueck in die Mitte und
     * arbeitete damit gegen die Geste.
     */
    if (ereignis.pointerType && ereignis.pointerType !== 'mouse') return;
    if (this.laeuft) return;

    const ziel = ereignis.target;
    if (!(ziel instanceof Element)) return;

    const karte = ziel.closest('li');
    if (!karte || !this.track.contains(karte)) return;

    const spur = this.track.getBoundingClientRect();
    const kasten = karte.getBoundingClientRect();
    const links = kasten.left - spur.left + this.track.scrollLeft;
    const rechts = links + kasten.width;
    const sichtbarLinks = this.track.scrollLeft;
    const sichtbarRechts = sichtbarLinks + this.track.clientWidth;
    const rand = 8;

    if (links < sichtbarLinks - 1) {
      this.animiereZu(links - rand);
    } else if (rechts > sichtbarRechts + 1) {
      this.animiereZu(rechts - this.track.clientWidth + rand);
    }
  }

  /** @param {number} richtung -1 zurueck, 1 weiter */
  scrollByCard(richtung) {
    const karte = this.track.querySelector('li');
    const abstand = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const schritt = karte ? karte.getBoundingClientRect().width + abstand : this.track.clientWidth;
    this.animiereZu(this.track.scrollLeft + richtung * schritt);
  }

  /**
   * Eigene Animation statt scrollBy mit behavior: 'smooth'.
   *
   * Zwei Gruende: die Pfeile lassen sich waehrend der Bewegung aktualisieren,
   * ohne auf scroll-Ereignisse angewiesen zu sein, und das Verhalten ist in
   * jeder Umgebung gleich - manche Browser fuehren smooth-Scrollen nicht aus.
   *
   * @param {number} ziel
   */
  animiereZu(ziel) {
    const maximum = this.track.scrollWidth - this.track.clientWidth;
    const ende = Math.max(0, Math.min(maximum, ziel));
    const start = this.track.scrollLeft;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.track.scrollLeft = ende;
      this.update();
      return;
    }

    // Verhindert, dass ein Hover waehrend der Bewegung eine zweite Animation
    // startet und beide gegeneinander laufen.
    this.laeuft = true;

    const dauer = 320;
    const beginn = performance.now();
    const schritt = (jetzt) => {
      const anteil = Math.min(1, (jetzt - beginn) / dauer);
      const weich = 1 - Math.pow(1 - anteil, 3);
      this.track.scrollLeft = start + (ende - start) * weich;
      this.update();
      if (anteil < 1) {
        requestAnimationFrame(schritt);
      } else {
        this.laeuft = false;
      }
    };
    requestAnimationFrame(schritt);
  }

  update() {
    const maximum = this.track.scrollWidth - this.track.clientWidth;
    const position = this.track.scrollLeft;
    // Toleranz gegen Rundungsfehler bei gebrochenen Pixelbreiten
    const toleranz = 2;

    const amAnfang = position <= toleranz;
    const amEnde = position >= maximum - toleranz;

    this.setze(this.prev, amAnfang);
    this.setze(this.next, amEnde);

    // Weiche Kante nur dort, wo tatsaechlich etwas dahinter verschwindet.
    this.classList.toggle('dp-scroller--kante-links', !amAnfang);
    this.classList.toggle('dp-scroller--kante-rechts', !amEnde);

    this.schreibeZaehler(position);
  }

  /**
   * Schreibt "3/7" in das Zaehlfeld, sofern es eines gibt.
   *
   * Gerechnet wird aus der Scrollposition und der Breite eines Elements - das
   * ist genauer als mitzuzaehlen, wie oft ein Pfeil gedrueckt wurde, denn
   * gewischt und getastet werden kann ebenso.
   *
   * @param {number} position Aktuelle Scrollposition.
   */
  schreibeZaehler(position) {
    if (!this.zaehler) return;

    const anzahl = this.track.children.length;
    if (!anzahl) return;

    const erstes = this.track.children[0];
    const abstand = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const schritt = erstes.getBoundingClientRect().width + abstand;
    if (!schritt) return;

    const nummer = Math.min(anzahl, Math.max(1, Math.round(position / schritt) + 1));
    const text = nummer + '/' + anzahl;
    if (this.zaehler.textContent.trim() !== text) this.zaehler.textContent = text;
  }

  /**
   * Zusaetzlich zur disabled-Eigenschaft wird eine Klasse gesetzt. Das
   * Ausblenden haengt damit nicht an der :disabled-Pseudoklasse, die von
   * anderen Regeln des Themes ueberschrieben werden kann.
   *
   * @param {HTMLButtonElement | null} knopf
   * @param {boolean} amEnde
   */
  setze(knopf, amEnde) {
    if (!knopf) return;
    knopf.disabled = amEnde;
    knopf.classList.toggle('dp-scroller__arrow--aus', amEnde);
  }
}

if (!customElements.get('dp-scroller')) {
  customElements.define('dp-scroller', DpScroller);
}

/*
 * Wie wird gerade bedient - mit dem Finger oder mit einem Zeiger?
 *
 * Die Frage entscheidet, ob eine Produktkarte beim Beruehren auf ihr zweites
 * Bild umschalten darf. Auf Telefonen bleibt :hover nach dem Antippen haengen,
 * das Bild wechselte also im Moment des Tippens. Die Medienabfrage "kein
 * Zeiger" allein genuegt dafuer nicht: nicht jedes Geraet meldet sich so,
 * manche geben beides an.
 *
 * Vermerkt wird deshalb, was zuletzt tatsaechlich benutzt wurde. Das Merkmal
 * steht am Wurzelelement, damit CSS es auswerten kann.
 */
function merkeEingabeart(art) {
  if (document.documentElement.dataset.dpEingabe !== art) {
    document.documentElement.dataset.dpEingabe = art;
  }
}

window.addEventListener(
  'pointerdown',
  (ereignis) => {
    merkeEingabeart(ereignis.pointerType === 'mouse' ? 'zeiger' : 'tipp');
  },
  { capture: true, passive: true }
);

// Aeltere Browser ohne Zeigerereignisse melden nur Beruehrungen.
window.addEventListener('touchstart', () => merkeEingabeart('tipp'), {
  capture: true,
  passive: true,
});

/* ------------------------------------------------------- Mehr anzeigen */

/**
 * Setzt die Kachel "Mehr anzeigen" auf die Hoehe des Bildes daneben.
 *
 * Sie steht in derselben Reihe wie die Produktkarten und war damit so hoch wie
 * eine ganze Karte - der Verweis sass also mittig zu Bild, Name und Preis
 * zusammen und damit sichtbar tiefer als die Bilder, an denen das Auge sich
 * ausrichtet. Gemessen wird die Bildflaeche der Nachbarkarte; die Hoehe steht
 * danach als Merkmal bereit, das Stilblatt richtet den Verweis darin mittig
 * aus.
 *
 * Gemessen und nicht gerechnet: das Seitenverhaeltnis der Karten ist eine
 * Einstellung des Abschnitts, und wie breit eine Karte ausfaellt, haengt an
 * der Zahl der sichtbaren Karten und am Fenster.
 */
const mehrBeobachter = new ResizeObserver(() => richteMehrAus());

function richteMehrAus() {
  for (const kachel of document.querySelectorAll('.dp-sub-brand__more-item')) {
    const bild = kachel.parentElement?.querySelector('.dp-sub-brand__product .resource-card__media');
    if (!bild) continue;

    // Aendert sich die Breite der Reihe, aendert sich auch die Bildhoehe.
    if (bild.dataset.dpGemessen === undefined) {
      bild.dataset.dpGemessen = '';
      mehrBeobachter.observe(bild);
    }

    const hoehe = Math.round(bild.getBoundingClientRect().height);
    if (hoehe <= 0) continue;

    // Nur bei echter Aenderung schreiben, sonst weckt der Beobachter sich selbst.
    if (kachel.style.getPropertyValue('--dp-bildhoehe') !== hoehe + 'px') {
      kachel.style.setProperty('--dp-bildhoehe', hoehe + 'px');
    }
  }
}

richteMehrAus();

/*
 * Nachgemessen wird, sobald sich etwas an der Hoehe aendern kann. Der
 * Beobachter oben deckt das eigentlich ab, aber er greift erst, wenn der
 * Browser wieder zeichnet - beim ersten Aufbau der Seite steht das Bild noch
 * ohne Hoehe da. Die drei Anlaesse hier kosten nichts und sorgen dafuer, dass
 * die Kachel auch dann sitzt, wenn die Bilder spaet eintreffen.
 */
window.addEventListener('load', richteMehrAus);
window.addEventListener('resize', richteMehrAus, { passive: true });
window.addEventListener('load', () => {
  for (const bild of document.querySelectorAll('.dp-sub-brand__scroller img')) {
    if (!bild.complete) bild.addEventListener('load', richteMehrAus, { once: true });
  }
});
